"use client";

import React, { useCallback, useMemo, useState } from "react";
import { Carousel, Image } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import HlsVideo from "../HlsVideo";
import { usePhotoViewTracker } from "../usePhotoViewTracker";
import { usePhotoImpressions } from "../usePhotoImpressions";

/**
 * Custom carousel arrow button — react-slick injects className, style, onClick;
 * we forward them to position the icon in the center of the button.
 */
const CarouselArrow = ({
  className,
  style,
  onClick,
  icon,
  label,
}: {
  className?: string;
  style?: React.CSSProperties;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  icon: React.ReactNode;
  label: string;
}) => (
  <button
    type="button"
    className={className}
    style={style}
    onClick={onClick}
    aria-label={label}
  >
    {icon}
  </button>
);

export interface MemberPhotoCarouselPhoto {
  id: string;
  src: string;
  alt?: string;
  type?: "image" | "video";
  thumbnail?: string;
  variants?: unknown;
}

export interface MemberPhotoCarouselProps {
  /** Photo objects with id, src, type, and optional thumbnail via variants. */
  photos: MemberPhotoCarouselPhoto[];
  /** Omit in previews to suppress tracking. */
  salonId?: string;
  /** Number of slides visible at once. Default 3 (matches Eddie's portfolio). */
  slidesToShow?: number;
  /** Break out of container padding to span full viewport (≤575px). */
  fullBleed?: boolean;
  /** Slide height in px (desktop). Mobile uses 180px. Default 240. */
  height?: number;
}

/**
 * Member portfolio carousel — mirrors Eddie's `DisplaySlider`.
 *
 * - Shows `slidesToShow` slides at once (default 3), each a fixed-height
 *   `object-fit: cover` tile (240px desktop / 180px mobile).
 * - White centered chevron arrows with drop-shadow, anchored at the slide
 *   mid-height so they don't drift while images load.
 * - Full-bleed only while the layout is stacked (≤575px).
 * - Video tiles and the lightbox both autoplay muted and loop (HlsVideo —
 *   Cloudflare Stream HLS), matching the classic photos page.
 * - Preview state is mirrored to the `photoId` query param for shareable links.
 */
export function MemberPhotoCarousel({
  photos,
  slidesToShow = 3,
  fullBleed = false,
  height = 240,
  salonId,
}: MemberPhotoCarouselProps) {
  const [photoId, setPhotoId] = useState<string | null>(null);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const tracker = usePhotoViewTracker(salonId);
  usePhotoImpressions(rootRef, salonId ? tracker.trackImpression : undefined, photos);
  React.useEffect(() => {
    if (photoId && photos.some((photo) => photo.id === photoId)) tracker.trackOpen(photoId);
  }, [photoId, photos, tracker]);

  // Sync the active preview from the URL on mount (shareable links).
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const idFromUrl = params.get("photoId");
    if (idFromUrl) setPhotoId(idFromUrl);
  }, []);

  const carouselSettings = useMemo(
    () => ({
      dots: false,
      infinite: photos.length > slidesToShow,
      speed: 500,
      slidesToScroll: 1,
      slidesToShow: Math.min(slidesToShow, Math.max(photos.length, 1)),
      arrows: true,
      prevArrow: (
        <CarouselArrow
          icon={<LeftOutlined />}
          label="Previous portfolio image"
        />
      ),
      nextArrow: (
        <CarouselArrow
          icon={<RightOutlined />}
          label="Next portfolio image"
        />
      ),
    }),
    [photos.length, slidesToShow],
  );

  const onChangePreview = useCallback((id: string) => {
    if (!id) return;
    const url = new URL(window.location.href);
    url.searchParams.set("photoId", id);
    window.history.replaceState(null, "", url.toString());
    setPhotoId(id);
  }, []);

  const onClosePreview = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete("photoId");
    window.history.replaceState(null, "", url.toString());
    setPhotoId(null);
  }, []);

  // Prepare preview items with consistent src
  const previewItems = useMemo(
    () =>
      photos.map((p) => ({
        ...p,
        src: p.src, // use original src for lightbox
      })),
    [photos],
  );

  // Lightbox settings
  const previewSettings = useMemo(() => {
    const currentIndex = photoId
      ? photos.findIndex((photo) => photo.id === photoId)
      : 0;

    return {
      visible: !!photoId,
      current: currentIndex !== -1 ? currentIndex : 0,
      toolbarRender: () => null,
      imageRender: (
        originNode: React.ReactNode,
        { current }: { current: number },
      ) => {
        const currentPhoto = previewItems[current];
        if (currentPhoto?.type === "video") {
          return (
            <HlsVideo
              src={currentPhoto.src}
              poster={currentPhoto.thumbnail}
              autoPlay
              muted
              loop
              playsInline
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
          );
        }
        return originNode;
      },
      onChange: (current: number) => {
        const selectedPhoto = photos[current];
        if (selectedPhoto?.id) onChangePreview(selectedPhoto.id);
      },
      onVisibleChange: (visible: boolean) => {
        if (!visible) onClosePreview();
      },
    };
  }, [photoId, photos, previewItems, onChangePreview, onClosePreview]);

  if (photos.length === 0) return null;

  return (
    <div
      ref={rootRef}
      className={`openings-member-carousel${fullBleed ? " is-full-bleed" : ""}`}
      style={{ ["--carousel-h" as string]: `${height}px` }}
    >
      <style>{carouselCss}</style>
      <Image.PreviewGroup items={previewItems} preview={previewSettings}>
        <Carousel {...carouselSettings}>
          {photos.map((photo) =>
            photo.type === "video" ? (
              <div
                key={photo.id}
                data-work-photo-id={photo.id}
                className="openings-member-carousel-slide is-video"
                role="button"
                tabIndex={0}
                aria-label={photo.alt || "Open video preview"}
                onClick={() => onChangePreview(photo.id)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onChangePreview(photo.id);
                  }
                }}
              >
                <HlsVideo
                  src={photo.src}
                  poster={photo.thumbnail}
                  autoPlay
                  muted
                  loop
                  playsInline
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    pointerEvents: "none",
                  }}
                />
              </div>
            ) : (
              <button
                key={photo.id}
                data-work-photo-id={photo.id}
                type="button"
                className="openings-member-carousel-slide is-image"
                aria-label={
                  photo.alt
                    ? `Open ${photo.alt} preview`
                    : "Open photo preview"
                }
                onClick={() => onChangePreview(photo.id)}
              >
                <Image
                  src={photo.src || "/logo.png"}
                  alt={photo.alt || "Photo"}
                  width="100%"
                  height="100%"
                  preview={false}
                />
              </button>
            ),
          )}
        </Carousel>
      </Image.PreviewGroup>
    </div>
  );
}

/**
 * Scoped styles ported from Eddie's `#DisplaySlider` rules. Lives inline
 * because template-components has no global stylesheet.
 */
const carouselCss = `
.openings-member-carousel {
  position: relative;
  line-height: 0;
  min-height: var(--carousel-h);
}
.openings-member-carousel.is-full-bleed {
  /* Only break out while the layout is stacked (≤575px). */
}
@media (max-width: 575px) {
  .openings-member-carousel.is-full-bleed {
    width: 100vw;
    margin-left: calc(-50vw + 50%);
  }
}
.openings-member-carousel .slick-slider,
.openings-member-carousel .slick-list,
.openings-member-carousel .slick-track,
.openings-member-carousel .slick-slide > div,
.openings-member-carousel .openings-member-carousel-slide,
.openings-member-carousel .ant-image {
  height: var(--carousel-h);
}
.openings-member-carousel .openings-member-carousel-slide {
  width: 100%;
  padding: 0;
  overflow: hidden;
  cursor: pointer;
  border: 0;
  background: #f5f5f5;
  color: inherit;
  text-align: inherit;
}
.openings-member-carousel .openings-member-carousel-slide:focus-visible {
  outline: 2px solid var(--openings-color-text, #111);
  outline-offset: -2px;
}
.openings-member-carousel .ant-image,
.openings-member-carousel .ant-image-img {
  width: 100% !important;
}
.openings-member-carousel .ant-image-img {
  height: var(--carousel-h) !important;
  object-fit: cover;
  object-position: center;
}
@media (max-width: 575px) {
  .openings-member-carousel { --carousel-h: 180px; }
}
.openings-member-carousel .slick-prev,
.openings-member-carousel .slick-next {
  z-index: 3;
  width: 40px;
  height: 40px;
  display: flex !important;
  align-items: center;
  justify-content: center;
  top: calc(var(--carousel-h) / 2);
  transform: translateY(-50%);
  color: #ffffff;
  font-size: 22px;
}
.openings-member-carousel .slick-prev { left: 12px; }
.openings-member-carousel .slick-next { right: 12px; }
.openings-member-carousel .slick-prev::before,
.openings-member-carousel .slick-next::before,
.openings-member-carousel .slick-prev::after,
.openings-member-carousel .slick-next::after {
  content: "" !important;
  display: none !important;
}
.openings-member-carousel .slick-prev .anticon,
.openings-member-carousel .slick-next .anticon {
  filter: drop-shadow(0 1px 5px rgba(0, 0, 0, 0.65));
}
`;

export default MemberPhotoCarousel;
